import { useSeoMeta } from '@unhead/react';
import { AppShell } from '@/components/AppShell';
import { useSocial } from '@/contexts/SocialContext';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { NoteCard } from '@/components/note';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';

export function BookmarksPage() {
  useSeoMeta({ title: 'Bookmarks — Jumble', description: 'Your saved notes.' });
  const { bookmarks, toggleBookmark } = useSocial();
  const { nostr } = useNostr();

  const query = useQuery({
    queryKey: ['jumble', 'bookmarks', bookmarks.join(',')],
    enabled: bookmarks.length > 0,
    queryFn: async ({ signal }) => {
      const events = (await nostr.query([{ ids: bookmarks.slice(0, 100) }], { signal: signal ?? AbortSignal.timeout(5000) })) as NostrEvent[];
      const order = new Map(bookmarks.map((id, i) => [id, i]));
      return events.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    },
  });

  return (
    <AppShell title="Bookmarks">
      {bookmarks.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground">
          No bookmarks yet. Tap the bookmark icon on any note to save it here (stored on this device).
        </p>
      ) : query.isLoading ? (
        <div className="p-4"><Skeleton className="h-20 w-full" /></div>
      ) : (query.data ?? []).length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground">Saved notes couldn't be fetched from relays right now.</p>
      ) : (
        <>
          {(query.data ?? []).map((e) => <NoteCard key={e.id} event={e} />)}
          {bookmarks.filter((id) => !(query.data ?? []).some((e) => e.id === id)).map((id) => (
            <div key={id} className="flex items-center justify-between border-b px-4 py-3 text-sm text-muted-foreground">
              <span className="font-mono">Missing note {id.slice(0, 12)}…</span>
              <Button size="icon" variant="ghost" onClick={() => toggleBookmark(id)} aria-label="Remove bookmark"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </>
      )}
      <div className="pb-20 md:pb-4" />
    </AppShell>
  );
}
